import { useState } from 'react';
import { Link } from 'wouter';
import { Show } from '@clerk/react';
import { useGetHomeSummary, useGetMe, getGetMeQueryKey, useListGames, getListGamesQueryKey } from '@workspace/api-client-react';
import { Navbar } from '@/components/Navbar';
import { GameCard } from '@/components/GameCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Trophy, TrendingUp, Sparkles, Flame, Search, ArrowRight, Calendar, Zap, Terminal } from 'lucide-react';

export default function Home() {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: summary, isLoading: summaryLoading } = useGetHomeSummary();
  const { data: me } = useGetMe({ query: { retry: false, queryKey: getGetMeQueryKey() } });
  const { data: searchResults, isLoading: searchLoading } = useListGames(
    { search: searchQuery },
    { query: { enabled: searchQuery.length > 0, queryKey: getListGamesQueryKey({ search: searchQuery }) } }
  );

  return (
    <div className="min-h-[100dvh] bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-primary/20 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-secondary/20 blur-[120px] rounded-full pointer-events-none" />
      
      <Navbar />

      <div className="mx-auto max-w-7xl px-4 py-4 sm:py-5 sm:px-6 lg:px-8 relative z-10">
        {/* Hero Section */}
        <div className="mb-6 text-center animate-slide-up">
          <Show when="signed-in">
            <div className="mb-3 inline-flex items-center gap-3 rounded-none bg-background/80 backdrop-blur-md px-6 py-1 text-sm font-bold text-primary border border-primary/50 shadow-[0_0_20px_rgba(0,217,255,0.2)] uppercase tracking-widest">
              <Flame className="h-5 w-5 animate-pulse" />
              {me?.currentStreak || 0} day streak
              <span className="mx-2 text-primary/50">|</span>
              Level {me?.level || 1}
              <span className="mx-2 text-primary/50">|</span>
              <span className="text-secondary">{me?.xp || 0} XP</span>
            </div>
          </Show>

          <h1 className="mb-3 text-3xl font-black tracking-tighter sm:text-4xl lg:text-5xl font-display uppercase leading-[0.9]">
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white via-primary to-white drop-shadow-[0_0_15px_rgba(255,255,255,0.2)] pb-2">
              Sports Trivia
            </span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary via-secondary to-primary drop-shadow-[0_0_20px_rgba(176,38,255,0.4)] relative">
              <span className="absolute inset-0 bg-clip-text bg-gradient-to-r from-primary via-secondary to-primary opacity-50 blur-sm pointer-events-none">Sports Trivia</span>
              On Quick Play
            </span>
          </h1>
          <p className="mx-auto mb-4 max-w-3xl text-xs sm:text-sm text-muted-foreground font-mono leading-relaxed">
            30-SECOND VISUAL PUZZLES <span className="text-primary/50">|</span> DAILY CHALLENGES <span className="text-primary/50">|</span> BOREDOM SOLVED
          </p>

          <div className="mx-auto mb-4 max-w-xl relative group">
            <div className="absolute inset-0 bg-primary/20 blur-md rounded-none group-focus-within:bg-primary/40 transition-all duration-500" />
            <div className="relative flex items-center bg-background border-2 border-primary/50 focus-within:border-primary glow-primary transition-all">
              <Terminal className="ml-4 h-5 w-5 text-primary animate-pulse" />
              <Input
                type="search"
                placeholder="Search games..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-4 h-10 text-sm font-mono uppercase tracking-widest bg-transparent border-none focus-visible:ring-0 placeholder:text-muted-foreground"
                data-testid="input-search-games"
              />
            </div>
          </div>

          <Show when="signed-out">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link href="/sign-up">
                <Button size="lg" className="h-10 px-6 text-sm font-display uppercase tracking-[0.2em] font-bold bg-primary text-primary-foreground hover:bg-primary/90 glow-primary border-2 border-primary rounded-none shadow-[0_0_30px_rgba(0,217,255,0.4)] hover:-translate-y-1 transition-transform" data-testid="button-hero-get-started">
                  Get Started <Zap className="ml-3 h-5 w-5" />
                </Button>
              </Link>
              <Link href="/games">
                <Button size="lg" variant="outline" className="h-10 px-6 text-sm font-display uppercase tracking-[0.2em] font-bold border-2 border-primary/50 text-primary hover:bg-primary/10 hover:border-primary rounded-none transition-all" data-testid="button-hero-browse">
                  Browse Games
                </Button>
              </Link>
            </div>
          </Show>
        </div>

        {/* Search Results */}
        {searchQuery && (
          <div className="mb-16 animate-slide-up">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px bg-primary/50 flex-1" />
              <h2 className="text-3xl font-bold font-display uppercase tracking-widest text-primary text-shadow-neon-cyan">
                Search Results
              </h2>
              <div className="h-px bg-primary/50 flex-1" />
            </div>
            
            {searchLoading ? (
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-80 rounded-xl bg-muted/50 border border-primary/20" />
                ))}
              </div>
            ) : searchResults && searchResults.length > 0 ? (
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 stagger-children">
                {searchResults.map((game) => (
                  <GameCard key={game.slug} game={game} />
                ))}
              </div>
            ) : (
              <div className="rounded-none border-2 border-dashed border-primary/30 bg-primary/5 p-16 text-center">
                <p className="text-xl font-mono text-primary animate-pulse">NO GAMES FOUND FOR "{searchQuery}"</p>
              </div>
            )}
          </div>
        )}

        {!searchQuery && (
          <>
            {/* Daily Games */}
            {summaryLoading ? (
              <div className="mb-16">
                <Skeleton className="mb-8 h-10 w-64 bg-primary/20" />
                <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-80 rounded-none bg-muted/50 border border-primary/20" />
                  ))}
                </div>
              </div>
            ) : summary && summary.dailyGames.length > 0 ? (
              <div className="mb-20">
                <div className="mb-8 flex items-end justify-between border-b-2 border-primary/20 pb-4">
                  <h2 className="flex items-center gap-3 text-3xl font-bold font-display uppercase tracking-widest">
                    <Calendar className="h-8 w-8 text-primary" />
                    Today's Daily
                  </h2>
                  <Link href="/games?sort=new">
                    <Button variant="ghost" className="font-bold uppercase tracking-widest text-primary hover:bg-primary/10 hover:text-primary rounded-none" data-testid="link-view-all-daily">
                      View All <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 stagger-children">
                  {summary.dailyGames.slice(0, 3).map((game) => (
                    <GameCard key={game.slug} game={game} />
                  ))}
                </div>
              </div>
            ) : null}

            {/* Trending */}
            {summary && summary.trending.length > 0 && (
              <div className="mb-20">
                <div className="mb-8 flex items-end justify-between border-b-2 border-secondary/20 pb-4">
                  <h2 className="flex items-center gap-3 text-3xl font-bold font-display uppercase tracking-widest">
                    <TrendingUp className="h-8 w-8 text-secondary" />
                    Trending Now
                  </h2>
                  <Link href="/games?sort=trending">
                    <Button variant="ghost" className="font-bold uppercase tracking-widest text-secondary hover:bg-secondary/10 hover:text-secondary rounded-none" data-testid="link-view-all-trending">
                      View All <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 stagger-children">
                  {summary.trending.slice(0, 3).map((game) => (
                    <GameCard key={game.slug} game={game} />
                  ))}
                </div>
              </div>
            )}

            {/* Featured */}
            {summary && summary.featured.length > 0 && (
              <div className="mb-20">
                <div className="mb-8 flex items-end justify-between border-b-2 border-primary/20 pb-4">
                  <h2 className="flex items-center gap-3 text-3xl font-bold font-display uppercase tracking-widest text-shadow-neon-cyan">
                    <Sparkles className="h-8 w-8 text-primary" />
                    Featured Selection
                  </h2>
                </div>
                <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 stagger-children">
                  {summary.featured.slice(0, 6).map((game) => (
                    <GameCard key={game.slug} game={game} />
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-12">
              {/* Sports Categories */}
              <div className="lg:col-span-2">
                {summary && summary.sports.length > 0 && (
                  <div>
                    <h2 className="mb-8 text-3xl font-bold font-display uppercase tracking-widest border-b-2 border-border/50 pb-4">
                      Browse by Sport
                    </h2>
                    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 stagger-children">
                      {summary.sports.map((sport) => (
                        <Link key={sport.key} href={`/games?sport=${sport.key}`}>
                          <div className="group rounded-none border border-primary/20 bg-background/50 p-6 text-center transition-all duration-300 hover:border-primary hover:bg-primary/5 hover:shadow-[0_0_20px_rgba(0,217,255,0.2)] cursor-pointer relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0 translate-x-[-100%] group-hover:animate-slide-right pointer-events-none" />
                            <h3 className="mb-2 font-display font-bold text-xl uppercase tracking-widest group-hover:text-primary transition-colors">
                              {sport.name}
                            </h3>
                            <p className="text-xs font-mono text-muted-foreground group-hover:text-primary/70 transition-colors">
                              {sport.gameCount} {sport.gameCount === 1 ? 'GAME' : 'GAMES'}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Top Streaks */}
              <div className="lg:col-span-1">
                {summary && summary.topStreaks.length > 0 && (
                  <div>
                    <h2 className="mb-8 text-3xl font-bold font-display uppercase tracking-widest border-b-2 border-secondary/30 pb-4 flex items-center gap-3">
                      <Trophy className="h-8 w-8 text-secondary" />
                      High Scores
                    </h2>
                    <div className="rounded-none border-2 border-secondary/30 bg-background relative">
                      {/* Grid overlay */}
                      <div className="absolute inset-0 bg-[linear-gradient(hsl(280_100%_60%_/_0.1)_1px,_transparent_1px),_linear-gradient(90deg,_hsl(280_100%_60%_/_0.1)_1px,_transparent_1px)] bg-[size:10px_10px] opacity-30 pointer-events-none" />
                      
                      <div className="relative z-10">
                        {summary.topStreaks.slice(0, 5).map((entry, idx) => (
                          <Link key={entry.username} href={`/u/${entry.username}`}>
                            <div className="flex items-center justify-between border-b border-secondary/20 last:border-0 p-4 hover:bg-secondary/10 transition-colors cursor-pointer group">
                              <div className="flex items-center gap-4">
                                <div className={`flex h-10 w-10 items-center justify-center rounded-none border border-secondary/50 font-display text-xl font-bold ${idx === 0 ? 'bg-secondary text-secondary-foreground glow-secondary' : 'bg-background text-secondary group-hover:bg-secondary/20'}`}>
                                  {entry.rank}
                                </div>
                                <div className="flex flex-col">
                                  <span className="font-bold font-mono tracking-widest text-lg group-hover:text-secondary transition-colors">{entry.username}</span>
                                  {entry.level && (
                                    <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
                                      Level {entry.level}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-secondary font-mono text-xl font-bold">
                                {entry.value} <span className="text-[10px] text-muted-foreground mt-1">DAYS</span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
